// Troca de plano do Reservei no visual do checkout novo (2026-10-20).
//
// SÓ APRESENTAÇÃO. Passe, prévia da troca, cobrança, acompanhamento do
// pagamento e volta pro app continuam no PlanChangePage (pages/checkout/
// trocar.tsx) — o mesmo código que já cobra certo hoje. Esta tela recebe o
// estado pronto e devolve as escolhas; a conta do valor vem de planChangeMath,
// a mesma do visual padrão (outros emissores de passe).

import { ArrowRight, Check, CircleAlert, CreditCard, Loader2 } from 'lucide-react';
import { useReducedMotion } from 'framer-motion';
import { useState, type Dispatch, type SetStateAction } from 'react';

import type { ChargePayResult } from '../../lib/charge-checkout-api';
import { maskCep } from '../../lib/masks';
import { cycleSuffix, type EligibleUpgrade, type PlanChangePreview } from '../../lib/plan-change-api';
import { planChangeMath } from '../../lib/plan-change-math';
import { PixIcon } from '../checkout-public/branded-icons';
import {
  BlockedScreen,
  FieldError,
  Footer,
  Header,
  LoadingScreen,
  PixQrBlock,
  Price,
  RESERVEI_FALLBACK_URL,
  SelectionHighlight,
  SuccessMark,
  displayPlanName,
  formatCardNumber,
  formatExpiry,
  formatPrice,
  isExpiryValid,
  onlyDigits,
  parsePixExpiration,
  safeUrl,
  splitExpiry,
  toQrSrc,
  useCheckoutPageMeta,
  useIsDesktop,
} from './shell';

export type PlanChangeMethod = 'pix' | 'credit_card';

export interface PlanChangeCard {
  number: string;
  holder_name: string;
  expiry_month: string;
  expiry_year: string;
  ccv: string;
  postal_code: string;
  address_number: string;
}

/** Pagamento em andamento nesta tela (mesma forma do PendingPayment do trocar.tsx). */
export interface PlanChangePayment {
  chargeId: string;
  method: PlanChangeMethod;
  pix: ChargePayResult | null;
  fallbackUrl: string | null;
}

export interface ReserveiPlanChangeProps {
  preview: PlanChangePreview | null;
  loadError: string | null;
  /** `undefined` = não suspensa; `null` = suspensa sem fatura pagável achada. */
  overdueUrl: string | null | undefined;
  /** Cancelamento agendado (ainda com acesso) — a tela oferece "manter assinatura". */
  pendingCancellation?: { endsAt: string | null } | undefined;
  keeping?: boolean;
  keepError?: string | null;
  onKeep?: () => void;
  selected: EligibleUpgrade | null;
  onSelect: (plan: EligibleUpgrade) => void;
  method: PlanChangeMethod | null;
  onMethod: (method: PlanChangeMethod) => void;
  card: PlanChangeCard;
  onCard: Dispatch<SetStateAction<PlanChangeCard>>;
  starting: boolean;
  done: boolean;
  scheduledFor: string | null;
  payment: PlanChangePayment | null;
  paid: boolean;
  backUrl: string;
  autoReturn: boolean;
  returnIn: number;
  onConfirm: () => void;
}

/** 'YYYY-MM-DD' → 'DD/MM', sem `new Date` (não volta 1 dia por fuso). */
function formatDay(ymd: string | null | undefined): string {
  if (!ymd) return '—';
  const [, m, d] = ymd.split('-');
  return m && d ? `${d}/${m}` : ymd;
}

function plural(n: number, one: string, many: string): string {
  return n === 1 ? one : many;
}

function money(value: string | null | undefined): string {
  const n = Number(value);
  return Number.isFinite(n) ? formatPrice(n) : '—';
}

export function ReserveiPlanChange(props: ReserveiPlanChangeProps): JSX.Element {
  const {
    preview,
    loadError,
    overdueUrl,
    pendingCancellation,
    keeping = false,
    keepError = null,
    onKeep,
    selected,
    onSelect,
    method,
    onMethod,
    card,
    onCard,
    starting,
    done,
    scheduledFor,
    payment,
    paid,
    backUrl,
    autoReturn,
    returnIn,
    onConfirm,
  } = props;
  useCheckoutPageMeta('Trocar plano · Reservei');
  const reducedMotion = useReducedMotion() !== false;
  const isDesktop = useIsDesktop();
  const [expiry, setExpiry] = useState(() =>
    card.expiry_month && card.expiry_year ? `${card.expiry_month}/${card.expiry_year.slice(-2)}` : '',
  );
  const [showCardErrors, setShowCardErrors] = useState(false);

  const homeUrl = preview?.saas_url || RESERVEI_FALLBACK_URL;
  const backTarget = backUrl || homeUrl;
  const links = { homeUrl, backTarget, helpUrl: safeUrl('/suporte', homeUrl) };

  if (loadError) {
    return <BlockedScreen links={links} title="Não deu para continuar" message={loadError} />;
  }

  // Cancelamento agendado (27/10): antes caía na COMPRA e virava uma 2ª
  // assinatura cobrada em cima do período já pago.
  if (pendingCancellation) {
    return (
      <BlockedScreen
        links={links}
        title="Seu cancelamento está agendado"
        message={`${
          pendingCancellation.endsAt ? `Seu acesso continua até ${formatDay(pendingCancellation.endsAt)}. ` : ''
        }Pra trocar de plano, primeiro mantenha a assinatura — a cobrança volta a seguir o ciclo de sempre.${
          keepError ? ` ${keepError}` : ''
        }`}
        action={
          <button
            type="button"
            className="reservei-checkout__primary-link"
            onClick={onKeep}
            disabled={keeping}
          >
            {keeping ? 'Mantendo…' : 'Manter assinatura'}
            <ArrowRight size={18} aria-hidden="true" />
          </button>
        }
      />
    );
  }

  if (overdueUrl !== undefined) {
    return (
      <BlockedScreen
        links={links}
        title="Sua assinatura está pausada"
        message="Ficou uma fatura em aberto, então o acesso foi pausado. Assim que o pagamento confirmar, seu plano volta sozinho — e aí dá para trocar de plano normalmente."
        action={
          overdueUrl ? (
            <a className="reservei-checkout__primary-link" href={overdueUrl}>
              Pagar agora
              <ArrowRight size={18} aria-hidden="true" />
            </a>
          ) : (
            <a className="reservei-checkout__primary-link" href={links.helpUrl} target="_blank" rel="noopener noreferrer">
              Falar com o suporte
              <ArrowRight size={18} aria-hidden="true" />
            </a>
          )
        }
      />
    );
  }

  if (!preview) return <LoadingScreen links={links} label="Carregando a troca de plano" />;

  const currentName = displayPlanName(preview.current_plan.name);

  if (preview.eligible_upgrades.length === 0) {
    return (
      <BlockedScreen
        links={links}
        title="Nenhum plano para trocar"
        message={`Você está no ${currentName} e não há outro plano disponível agora.`}
      />
    );
  }

  const math = planChangeMath(preview, selected);
  const { isRenewal, isDowngrade, renewalAmount, renewalHasDiscount, amountNow, isFree, needsPayment, daysLeft } = math;
  const selectedName = selected ? displayPlanName(selected.name) : null;
  const chosenMethod: PlanChangeMethod = method ?? 'pix';
  const cardDigits = onlyDigits(card.number);
  const cardErrors = {
    number: cardDigits.length < 13 ? 'Número do cartão incompleto.' : '',
    holder: card.holder_name.trim().length < 2 ? 'Informe o nome como está no cartão.' : '',
    expiry: !isExpiryValid(expiry) ? 'Validade inválida (MM/AA).' : '',
    cvv: card.ccv.length < 3 ? 'CVV com 3 ou 4 números.' : '',
  };
  const cardInvalid = Object.values(cardErrors).some(Boolean);
  const shownCardError = (key: keyof typeof cardErrors): string => (showCardErrors ? cardErrors[key] : '');

  type Stage = 'choose' | 'pix' | 'card' | 'done';
  const stage: Stage = done ? 'done' : payment ? (payment.method === 'pix' ? 'pix' : 'card') : 'choose';

  const totalLabel = !selected
    ? `${currentName} · ${money(preview.current_plan.monthly_price)}${cycleSuffix(preview)}`
    : isDowngrade
      ? 'Nada a pagar agora'
      : `Total agora · ${formatPrice(amountNow)}`;
  const totalHint = !selected ? 'escolha o novo plano' : isRenewal ? 'renovação no plano novo' : isDowngrade ? `vale a partir de ${formatDay(selected.scheduled_for)}` : 'diferença do ciclo atual';

  const cta = ((): { label: string; disabled: boolean; action: () => void } | null => {
    if (stage === 'done') {
      return { label: 'Voltar ao Reservei', disabled: false, action: () => window.location.assign(backTarget) };
    }
    if (stage !== 'choose') return null;
    if (starting) return { label: 'Processando…', disabled: true, action: () => undefined };
    if (!selected) return { label: 'Escolha um plano', disabled: true, action: () => undefined };
    const label = isDowngrade
      ? 'Agendar mudança'
      : isFree
        ? 'Mudar agora'
        : chosenMethod === 'pix'
          ? 'Gerar Pix'
          : `Pagar ${formatPrice(amountNow)}`;
    return {
      label,
      disabled: false,
      action: () => {
        if (needsPayment && chosenMethod === 'credit_card' && cardInvalid) {
          setShowCardErrors(true);
          return;
        }
        onConfirm();
      },
    };
  })();

  const primaryButton = cta ? (
    <button
      type="button"
      className="reservei-checkout__dock-cta"
      disabled={cta.disabled}
      aria-busy={starting || undefined}
      onClick={(event) => {
        if (event.detail > 1) return;
        cta.action();
      }}
    >
      {starting && <Loader2 size={18} className="animate-spin" aria-hidden="true" />}
      {cta.label}
      {!starting && <ArrowRight size={18} aria-hidden="true" />}
    </button>
  ) : null;
  const totalBlock = (
    <div className="reservei-checkout__dock-total">
      <div className="reservei-checkout__dock-total-value">
        <span>{totalLabel}</span>
        <small>{totalHint}</small>
      </div>
    </div>
  );

  const explanation = !selected
    ? 'Escolha um plano ao lado para ver quanto fica.'
    : isRenewal
      ? `Esta é a renovação da sua assinatura, já no ${selectedName}. A fatura anterior${preview.suspended ? ' (que estava em aberto)' : ''} é cancelada e substituída por esta. ${
          preview.suspended
            ? 'Assim que o pagamento confirmar, seu acesso volta na hora, direto no plano novo.'
            : preview.first_purchase
              ? 'Assim que o pagamento confirmar, sua conta ativa direto no plano escolhido.'
              : 'Assim que o pagamento confirmar, o plano novo vale na hora.'
        }`
      : isDowngrade
        ? `Você não paga nada agora e continua no ${currentName} até ${formatDay(selected.scheduled_for)} — esse período já está pago. Depois, a mensalidade cai para ${money(selected.monthly_price)}.`
        : isFree
          ? `Você está em período de teste, então não há nada a pagar agora. O ${selectedName} vale na hora e a primeira cobrança sai no valor dele, na data já prevista.`
          : `Você não paga o valor cheio: paga só a diferença proporcional aos ${daysLeft} ${plural(daysLeft, 'dia que falta', 'dias que faltam')} do ciclo atual. Os ${money(selected.monthly_price)} por mês valem a partir da próxima cobrança.`;

  return (
    <div className="reservei-checkout">
      <Header {...links} />

      <main className="reservei-checkout__main">
        <div className="reservei-checkout__layout">
          <section className="reservei-checkout__intro" aria-labelledby="rc-change-title">
            <h1 id="rc-change-title" tabIndex={-1} aria-label="Troque seu plano">
              <span>Troque seu</span>
              <span>plano</span>
            </h1>
            <p>
              Hoje você está no {currentName}
              {isRenewal && preview.suspended ? ' (pausado)' : ''}.
            </p>
          </section>

          <aside className="reservei-checkout__summary" aria-label="Resumo da troca">
            <div className="reservei-checkout__summary-panel">
              <p className="reservei-checkout__eyebrow">Resumo</p>
              <div className="reservei-checkout__summary-heading">
                <span className="reservei-checkout__calendar" aria-hidden="true">
                  <img src="/checkout/reservei/calendario.png" alt="" />
                </span>
                <div className="reservei-checkout__summary-details">
                  <h2>Plano {selectedName ?? currentName}</h2>
                  <span className="reservei-checkout__summary-cycle-label">
                    {selected ? `Hoje: ${currentName}` : 'Seu plano atual'}
                  </span>
                </div>
                <div className="reservei-checkout__summary-price">
                  {selected ? (
                    <Price value={isDowngrade ? 0 : amountNow} suffix="agora" />
                  ) : (
                    <Price value={Number(preview.current_plan.monthly_price) || 0} suffix={cycleSuffix(preview)} />
                  )}
                </div>
              </div>

              {selected && (
                <dl className="reservei-checkout__summary-rows">
                  {isRenewal ? (
                    <>
                      <div>
                        <dt>Mensalidade do {selectedName}</dt>
                        <dd>{money(selected.renewal_price ?? selected.monthly_price)}</dd>
                      </div>
                      {renewalHasDiscount && (
                        <div>
                          <dt>Seu desconto vigente</dt>
                          <dd>
                            − {formatPrice(Number(selected.renewal_price ?? '0') - Number(selected.effective_renewal_price ?? '0'))}
                          </dd>
                        </div>
                      )}
                      <div>
                        <dt>Fatura anterior</dt>
                        <dd>Cancelada e substituída</dd>
                      </div>
                      <div>
                        <dt>Total agora</dt>
                        <dd>{money(renewalAmount)}</dd>
                      </div>
                    </>
                  ) : isDowngrade ? (
                    <>
                      <div>
                        <dt>Cobrança agora</dt>
                        <dd>Nenhuma</dd>
                      </div>
                      <div>
                        <dt>Passa a valer em</dt>
                        <dd>{formatDay(selected.scheduled_for)}</dd>
                      </div>
                      <div>
                        <dt>Nova mensalidade</dt>
                        <dd>{money(selected.monthly_price)}</dd>
                      </div>
                    </>
                  ) : (
                    <>
                      <div>
                        <dt>Diferença mensal</dt>
                        <dd>{formatPrice(Number(selected.monthly_price) - Number(preview.current_plan.monthly_price))}</dd>
                      </div>
                      <div>
                        <dt>Dias restantes do ciclo</dt>
                        <dd>
                          {daysLeft} {plural(daysLeft, 'dia', 'dias')}
                        </dd>
                      </div>
                      <div>
                        <dt>Nova mensalidade</dt>
                        <dd>{money(selected.monthly_price)}</dd>
                      </div>
                    </>
                  )}
                </dl>
              )}

              <p className="reservei-checkout__summary-note">{explanation}</p>
            </div>

            <div className="reservei-checkout__support">
              <img src="/checkout/reservei/aceno.png" alt="" />
              <p>
                <span>Dúvidas na troca?</span>
                <span>
                  <a className="reservei-checkout__inline-link" href={links.helpUrl} target="_blank" rel="noopener noreferrer">
                    Fale com o suporte do Reservei
                  </a>
                </span>
              </p>
            </div>
          </aside>

          <section className="reservei-checkout__form-column" aria-label="Troca de plano">
            {stage === 'choose' && (
              <div className="reservei-checkout__form">
                {isRenewal && (
                  <p className="reservei-checkout__alert reservei-checkout__alert--info">
                    {preview.suspended
                      ? 'Sua assinatura está pausada por falta de pagamento e a renovação está em aberto. Escolha qualquer plano: ao pagar, o acesso volta na hora, já no plano escolhido.'
                      : preview.first_purchase
                        ? 'Sua primeira mensalidade ainda está em aberto. Se preferir começar em outro plano, escolha abaixo — a fatura antiga é cancelada e você ativa direto no plano novo.'
                        : 'Sua renovação está em aberto. Você pode renovar no plano atual ou já renovar em outro — a fatura antiga é substituída pela nova.'}
                  </p>
                )}
                {isRenewal && preview.current_plan_payment_url && (
                  <a className="reservei-checkout__inline-link" href={preview.current_plan_payment_url}>
                    Pagar minha renovação no {currentName}
                  </a>
                )}
                {preview.pending_downgrade && (
                  <p className="reservei-checkout__alert reservei-checkout__alert--info">
                    Você já tem uma mudança agendada para {formatDay(preview.pending_downgrade.scheduled_for)}. Escolher
                    outro plano agora substitui esse agendamento.
                  </p>
                )}

                <fieldset className="reservei-checkout__payment">
                  <legend>Novo plano</legend>
                  <div className="reservei-checkout__plan-options">
                    {preview.eligible_upgrades.map((option) => {
                      const active = selected?.plan_id === option.plan_id;
                      const optionName = displayPlanName(option.name);
                      const upgrade = option.direction === 'upgrade';
                      return (
                        <button
                          type="button"
                          key={option.plan_id}
                          className={`reservei-checkout__plan-option${active ? ' is-selected' : ''}`}
                          aria-pressed={active}
                          onClick={() => onSelect(option)}
                        >
                          {active && <SelectionHighlight reducedMotion={reducedMotion} />}
                          <span className="reservei-checkout__plan-option-name">
                            <strong>{optionName}</strong>
                            <small>{money(option.monthly_price)}/mês</small>
                          </span>
                          <span className="reservei-checkout__plan-option-value">
                            {isRenewal ? (
                              <>
                                <small>Renove por</small>
                                <strong>{money(option.effective_renewal_price ?? option.renewal_price ?? option.monthly_price)}</strong>
                              </>
                            ) : upgrade ? (
                              <>
                                <small>Você paga agora</small>
                                <strong>{money(option.proration_amount)}</strong>
                                <small>
                                  {Number(option.proration_amount) > 0
                                    ? `proporcional a ${option.days_remaining} ${plural(option.days_remaining, 'dia', 'dias')}`
                                    : 'sem cobrança (em teste)'}
                                </small>
                              </>
                            ) : (
                              <>
                                <small>Sem cobrança agora</small>
                                <strong>a partir de {formatDay(option.scheduled_for)}</strong>
                                {option.monthly_savings && <small>economiza {money(option.monthly_savings)}/mês</small>}
                              </>
                            )}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </fieldset>

                {needsPayment && (
                  <fieldset className="reservei-checkout__payment">
                    <legend>Pagamento</legend>
                    <div className="reservei-checkout__payment-options">
                      <label className={chosenMethod === 'pix' ? 'is-selected' : ''}>
                        {chosenMethod === 'pix' && <SelectionHighlight reducedMotion={reducedMotion} />}
                        <input
                          type="radio"
                          name="rc-change-method"
                          value="pix"
                          checked={chosenMethod === 'pix'}
                          onChange={() => onMethod('pix')}
                        />
                        <span className="reservei-checkout__radio" aria-hidden="true" />
                        <PixIcon size={22} aria-label="" />
                        <span>Pix</span>
                      </label>
                      <label className={chosenMethod === 'credit_card' ? 'is-selected' : ''}>
                        {chosenMethod === 'credit_card' && <SelectionHighlight reducedMotion={reducedMotion} />}
                        <input
                          aria-label="Cartão de crédito"
                          type="radio"
                          name="rc-change-method"
                          value="credit_card"
                          checked={chosenMethod === 'credit_card'}
                          onChange={() => onMethod('credit_card')}
                        />
                        <span className="reservei-checkout__radio" aria-hidden="true" />
                        <CreditCard size={22} aria-hidden="true" />
                        <span>
                          <span className="reservei-checkout__method-label-full">Cartão de crédito</span>
                          <span className="reservei-checkout__method-label-short">Cartão</span>
                        </span>
                      </label>
                    </div>
                    <p className="reservei-checkout__payment-helper">
                      {chosenMethod === 'pix'
                        ? 'O QR Code aparece na próxima tela. O plano novo libera assim que o banco confirmar.'
                        : 'O cartão fica salvo e as próximas mensalidades saem sozinhas.'}
                    </p>

                    {chosenMethod === 'credit_card' && (
                      <div className="reservei-checkout__card-form">
                        <div className="reservei-checkout__field">
                          <label htmlFor="rc-change-card-number">Número do cartão</label>
                          <input
                            id="rc-change-card-number"
                            inputMode="numeric"
                            autoComplete="cc-number"
                            value={card.number}
                            aria-invalid={Boolean(shownCardError('number'))}
                            onChange={(event) => onCard((c) => ({ ...c, number: formatCardNumber(event.target.value) }))}
                          />
                          <FieldError id="rc-change-card-number-error" message={shownCardError('number')} />
                        </div>
                        <div className="reservei-checkout__field">
                          <label htmlFor="rc-change-card-name">Nome impresso no cartão</label>
                          <input
                            id="rc-change-card-name"
                            autoComplete="cc-name"
                            value={card.holder_name}
                            aria-invalid={Boolean(shownCardError('holder'))}
                            onChange={(event) => onCard((c) => ({ ...c, holder_name: event.target.value }))}
                          />
                          <FieldError id="rc-change-card-name-error" message={shownCardError('holder')} />
                        </div>
                        <div className="reservei-checkout__field-row">
                          <div className="reservei-checkout__field">
                            <label htmlFor="rc-change-card-expiry">Validade</label>
                            <input
                              id="rc-change-card-expiry"
                              inputMode="numeric"
                              autoComplete="cc-exp"
                              placeholder="MM/AA"
                              value={expiry}
                              aria-invalid={Boolean(shownCardError('expiry'))}
                              onChange={(event) => {
                                const next = formatExpiry(event.target.value);
                                setExpiry(next);
                                const { month, year } = splitExpiry(next);
                                onCard((c) => ({ ...c, expiry_month: month, expiry_year: year }));
                              }}
                            />
                            <FieldError id="rc-change-card-expiry-error" message={shownCardError('expiry')} />
                          </div>
                          <div className="reservei-checkout__field">
                            <label htmlFor="rc-change-card-cvv">CVV</label>
                            <input
                              id="rc-change-card-cvv"
                              inputMode="numeric"
                              autoComplete="cc-csc"
                              value={card.ccv}
                              aria-invalid={Boolean(shownCardError('cvv'))}
                              onChange={(event) => onCard((c) => ({ ...c, ccv: onlyDigits(event.target.value).slice(0, 4) }))}
                            />
                            <FieldError id="rc-change-card-cvv-error" message={shownCardError('cvv')} />
                          </div>
                        </div>
                        <div className="reservei-checkout__field-row">
                          <div className="reservei-checkout__field">
                            <label htmlFor="rc-change-card-postal">CEP do titular</label>
                            <input
                              id="rc-change-card-postal"
                              inputMode="numeric"
                              autoComplete="postal-code"
                              value={card.postal_code}
                              onChange={(event) => onCard((c) => ({ ...c, postal_code: maskCep(event.target.value) }))}
                            />
                          </div>
                          <div className="reservei-checkout__field">
                            <label htmlFor="rc-change-card-address">Número do endereço</label>
                            <input
                              id="rc-change-card-address"
                              maxLength={20}
                              value={card.address_number}
                              onChange={(event) => onCard((c) => ({ ...c, address_number: event.target.value }))}
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </fieldset>
                )}
              </div>
            )}

            {stage === 'pix' && payment && (
              <div className="reservei-checkout__demo-stage reservei-checkout__demo-result reservei-checkout__demo-result--pix">
                <div className="reservei-checkout__pix-shell">
                  <div className="reservei-checkout__pix-status-row">
                    <span className="reservei-checkout__pix-status">
                      <span className="reservei-checkout__pix-status-dot" aria-hidden="true" />
                      Aguardando pagamento
                    </span>
                  </div>
                  <div className="reservei-checkout__pix-title">
                    <PixIcon size={24} aria-label="" />
                    <h2 id="rc-change-pix-title">Pague com Pix</h2>
                    <p>Abra o app do seu banco, escolha Pix e escaneie o QR Code ou cole o código.</p>
                  </div>
                  {payment.pix && (payment.pix.pix_qr_image || payment.pix.pix_payload) ? (
                    <PixQrBlock
                      qrSrc={toQrSrc(payment.pix.pix_qr_image)}
                      payload={payment.pix.pix_payload}
                      amount={amountNow > 0 ? amountNow : null}
                      summary={`${currentName} → ${selectedName ?? ''}`}
                      expiresAt={parsePixExpiration(payment.pix.pix_expiration)}
                      waitingText={`Assim que o banco confirmar, o ${selectedName ?? 'plano novo'} é liberado e esta tela avança sozinha.`}
                    />
                  ) : (
                    <p className="reservei-checkout__alert" role="alert">
                      {/* Socorro na NOSSA página da fatura (gera o QR de novo na
                          mesma cobrança) — nunca a do Asaas (20/10). */}
                      Não conseguimos gerar o QR do Pix agora.{' '}
                      <a className="reservei-checkout__inline-link" href={`/checkout/cobranca/${payment.chargeId}`}>
                        Abrir a página da fatura
                      </a>
                      .
                    </p>
                  )}
                </div>
              </div>
            )}

            {stage === 'card' && (
              <div
                className="reservei-checkout__demo-stage reservei-checkout__demo-result reservei-checkout__demo-result--card"
                aria-live="polite"
              >
                {paid ? <Check size={22} aria-hidden="true" /> : <Loader2 size={22} className="animate-spin" aria-hidden="true" />}
                <div>
                  <strong>Confirmando o pagamento</strong>
                  <span>
                    Enviamos o cartão para o banco. Em geral é na hora — assim que confirmar, esta tela atualiza e o{' '}
                    {selectedName ?? 'plano novo'} é liberado. Não pague de novo.
                  </span>
                </div>
              </div>
            )}

            {stage === 'done' && (
              <div className="reservei-checkout__demo-stage reservei-checkout__success-stage" aria-labelledby="rc-change-done">
                <SuccessMark />
                <div className="reservei-checkout__success-copy">
                  <strong id="rc-change-done">{scheduledFor ? 'Mudança agendada' : `Você está no ${selectedName ?? 'plano novo'}`}</strong>
                  <p className="reservei-checkout__success-qualification">
                    {scheduledFor
                      ? `Você continua no ${currentName} até ${formatDay(scheduledFor)} — esse período já está pago. Depois passa para o ${selectedName ?? 'plano novo'}. Nada foi cobrado agora.`
                      : paid
                        ? `Recebemos seu pagamento e o ${selectedName ?? 'plano novo'} já está sendo liberado. A próxima cobrança sai no valor dele.`
                        : `O ${selectedName ?? 'plano novo'} já está liberado. Nada foi cobrado agora — a próxima cobrança sai no valor dele, na data já prevista.`}
                  </p>
                  {autoReturn && <p className="reservei-checkout__locked-note">Voltando pro Reservei em {returnIn}s…</p>}
                </div>
              </div>
            )}

            {isDesktop && (primaryButton || stage === 'choose') && (
              <div className="reservei-checkout__inline-actions">
                {totalBlock}
                {primaryButton}
              </div>
            )}

            {stage !== 'done' && stage !== 'choose' && (
              <p className="reservei-checkout__locked-note">
                <CircleAlert size={14} aria-hidden="true" /> Pode fechar esta página depois de pagar: a troca é confirmada
                pelo banco e o Reservei atualiza sozinho.
              </p>
            )}
          </section>
        </div>
      </main>

      {!isDesktop && (
        <div className="reservei-checkout__action-dock reservei-checkout__action-dock--simple">
          {totalBlock}
          {primaryButton}
        </div>
      )}

      <Footer />
    </div>
  );
}
